import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Anchor,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  ShieldCheck,
  TrendingUp,
  Zap,
} from 'lucide-react';

/**
 * PledgeTimeline
 * ------------------------------------------------
 * Vertical, per-agent event feed showing enrolment,
 * pledge deltas, attestations, uptime milestones,
 * batch settlements, and slash events (with recovery).
 *
 * `events` prop is optional. When absent, we synthesise
 * a realistic demo timeline from the profile so we can
 * design & review the UI before real event data lands.
 */

const EVENT_META = {
  enrollment: {
    icon: Anchor,
    color: 'text-cyan-300',
    ring: 'ring-cyan-400/30',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/25',
    label: 'Enrolled in Directory',
  },
  pledge_increase: {
    icon: ArrowUpRight,
    color: 'text-emerald-400',
    ring: 'ring-emerald-500/30',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/25',
    label: 'Pledge Increased',
  },
  pledge_decrease: {
    icon: ArrowDownRight,
    color: 'text-yellow-400',
    ring: 'ring-yellow-500/30',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/25',
    label: 'Pledge Reduced',
  },
  attestation: {
    icon: ShieldCheck,
    color: 'text-blue-300',
    ring: 'ring-blue-400/30',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/25',
    label: 'Attestation Added',
  },
  uptime_milestone: {
    icon: TrendingUp,
    color: 'text-emerald-300',
    ring: 'ring-emerald-400/30',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/25',
    label: 'Uptime Milestone',
  },
  batch_settled: {
    icon: CheckCircle2,
    color: 'text-emerald-300',
    ring: 'ring-emerald-400/30',
    bg: 'bg-emerald-500/[0.06]',
    border: 'border-emerald-500/15',
    label: 'Successful Batch',
  },
  slash_executed: {
    icon: Zap,
    color: 'text-red-400',
    ring: 'ring-red-500/40',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    label: 'Breach Record',
  },
  slash_recovered: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    ring: 'ring-emerald-500/30',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/25',
    label: 'Breach Noted',
  },
};

function fmtUsd(n) {
  if (n == null) return '-';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
}

function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function relative(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (d < 1) return 'today';
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

/** Synthesize a demo timeline from a profile so we can review the UX. */
function synthesizeDemoEvents(profile) {
  if (!profile) return [];
  const events = [];
  const enrolAt = new Date(profile.created_at || Date.now() - 90 * 24 * 3600 * 1000);
  const daysSinceEnrol = Math.max(
    1,
    Math.floor((Date.now() - enrolAt.getTime()) / (1000 * 60 * 60 * 24)),
  );
  const pledge = profile.pledge_amount_usdc || 0;
  const isPlatinum = profile.pledge_tier === 'Platinum';
  const isGold = profile.pledge_tier === 'Gold';
  const isBronze = profile.pledge_tier === 'Bronze';

  // Enrollment
  events.push({
    kind: 'enrollment',
    ts: enrolAt.toISOString(),
    title: `Enrolled with ${fmtUsd(pledge)} initial pledge`,
    detail: `Tier: ${profile.pledge_tier} · ${profile.verification_signals?.length || 0} verification signals`,
    tx: '0xabcd…' + Math.floor(Math.random() * 9999).toString(16),
  });

  if (isPlatinum || isGold) {
    events.push({
      kind: 'attestation',
      ts: new Date(enrolAt.getTime() + 3 * 24 * 3600 * 1000).toISOString(),
      title: 'GitHub identity attestation added',
      detail: 'Signed commit binding wallet ↔ github.com/kojiru-labs',
    });
    events.push({
      kind: 'attestation',
      ts: new Date(enrolAt.getTime() + 5 * 24 * 3600 * 1000).toISOString(),
      title: 'Domain (DNS TXT) attestation added',
      detail: `_kojiru.${profile.operator_identity || 'agent.example'} → wallet TXT record`,
    });
  }

  if (isPlatinum) {
    events.push({
      kind: 'pledge_increase',
      ts: new Date(enrolAt.getTime() + 21 * 24 * 3600 * 1000).toISOString(),
      title: `Pledge topped up +${fmtUsd(50000)}`,
      detail: 'Additional collateral posted before enterprise SLA onboarding',
      tx: '0x' + Math.floor(Math.random() * 0xffffff).toString(16),
    });
  }

  // Regular successful batches: collapse into 3 representative rollups
  events.push({
    kind: 'batch_settled',
    ts: new Date(Date.now() - 32 * 24 * 3600 * 1000).toISOString(),
    title: `${Math.floor((profile.successful_transactions || 100) * 0.6).toLocaleString()} successful settlements (last 60d)`,
    detail: `Zero disputes · ${fmtUsd((profile.transaction_volume_lifetime || 10000) * 0.6)} lifetime notional`,
  });


  events.push({
    kind: 'uptime_milestone',
    ts: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(),
    title: `${daysSinceEnrol >= 30 ? '30' : '7'}-day uptime: ${(profile.uptime_30d ?? 99.5).toFixed(2)}%`,
    detail: 'Above Silver-tier SLA floor · no breach record',
  });

  if (!isBronze) {
    events.push({
      kind: 'batch_settled',
      ts: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
      title: `${(profile.successful_transactions || 100).toLocaleString()} lifetime successful txns`,
      detail: `${fmtUsd(profile.transaction_volume_30d)} settled in the last 30 days`,
    });
  }

  // Sort newest → oldest
  return events.sort((a, b) => new Date(b.ts) - new Date(a.ts));
}

export default function PledgeTimeline({ profile, events }) {
  const list = useMemo(() => {
    if (Array.isArray(events) && events.length > 0) return events;
    return synthesizeDemoEvents(profile);
  }, [events, profile]);
  const isDemo = !Array.isArray(events) || events.length === 0;

  if (!profile || list.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
      data-testid="kypa-agent-timeline"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-red-400" />
          <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">
            Pledge &amp; Slash Timeline
          </h2>
        </div>
        {isDemo && (
          <span
            className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/20"
            data-testid="kypa-agent-timeline-demo-badge"
          >
            Preview · demo events
          </span>
        )}
      </div>

      <div className="relative pl-6">
        {/* vertical rail */}
        <div className="absolute left-2 top-1 bottom-1 w-px bg-gradient-to-b from-red-500/30 via-white/10 to-transparent" />

        <ol className="space-y-4">
          {list.map((ev, idx) => {
            const meta = EVENT_META[ev.kind] || EVENT_META.batch_settled;
            const Icon = meta.icon;
            return (
              <motion.li
                key={`${ev.kind}-${idx}-${ev.ts}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: idx * 0.04 }}
                className="relative"
                data-testid={`kypa-agent-timeline-event-${idx}`}
              >
                {/* dot */}
                <span
                  className={`absolute -left-[18px] top-1.5 w-3 h-3 rounded-full ring-4 ${meta.ring} ${meta.bg} border ${meta.border}`}
                />
                <div
                  className={`rounded-xl border ${meta.border} ${meta.bg} px-4 py-3 hover:bg-white/[0.02] transition-colors`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${meta.color}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-sm text-white font-medium leading-snug">
                        {ev.title}
                      </div>
                      {ev.detail && (
                        <div className="text-xs text-gray-400 mt-1 leading-relaxed">{ev.detail}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] text-gray-400 font-mono whitespace-nowrap">
                        {relative(ev.ts)}
                      </div>
                      <div className="text-[10px] text-gray-600 font-mono whitespace-nowrap">
                        {fmtDateTime(ev.ts)}
                      </div>
                      {ev.tx && (
                        <div className="text-[10px] text-red-300/70 font-mono whitespace-nowrap mt-1">
                          {ev.tx.slice(0, 10)}…
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>

      {isDemo && (
        <p className="text-[11px] text-gray-500 mt-5 leading-relaxed">
          Timeline events shown are illustrative: synthesized from directory metadata. Once the
          on-chain <span className="font-mono text-gray-400">KojiruACSHook</span> indexer streams
          slash and attestation events into <span className="font-mono text-gray-400">kypa_events</span>,
          this feed swaps to live data automatically.
        </p>
      )}
    </section>
  );
}
